"use client";

import Script from "next/script";
import { useState } from "react";
import { motion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { AuthorAvatar, EyebrowBadge, HeroGlow } from "@/components/ui";
import { BRAND_BG, EMAIL, GREEN } from "@/lib/constants";
import { ChevronRight, Play, Users, X } from "@/components/icons";
import ProgramOverviewVisual from "./program-overview-visual";
import RequirementsVisual from "./requirements-visual";
import ModalShell from "./modal-shell";
import dynamic from "next/dynamic";

const ApplyModal = dynamic(() => import("./apply-modal"), { ssr: false, loading: () => null });
const ReferModal = dynamic(() => import("./refer-modal"), { ssr: false, loading: () => null });
import { FAQS } from "./faq-data";
import { LOOM_EMBED_URL, BENEFITS, STEPS, TESTIMONIALS, ASSURANCES } from "./data";

const APPLY_STEPS = [
  {
    title: "Run a desktop speed test",
    description: (
      <>
        On your computer, while connected to WiFi or ethernet, go to{" "}
        <a
          href="https://www.speedtest.net"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline underline-offset-2 transition-opacity hover:opacity-80"
          style={{ color: "var(--foreground)" }}
        >
          speedtest.net
        </a>{" "}
        and record the internet speed by taking a screenshot and saving the test URL.
      </>
    ),
  },
  {
    title: "Run a mobile speed test",
    description: (
      <>
        On your phone, test both your WiFi and 5G — install a speed test app on the same phone and
        record both readings.{" "}
        <span style={{ color: "var(--foreground)" }} className="font-medium">
          Please make sure you&apos;re using a supported US mobile carrier.
        </span>
      </>
    ),
  },
  {
    title: "Fill out the form (or book a call)",
    description: (
      <>
        Fill out the form on this page or schedule a call with us. After you collect the data, the
        rest of the process only takes 3–4 minutes — all that&apos;s left to fill out are the
        basics.
      </>
    ),
  },
] as const;

export default function ProxidizeHostingProgramSection() {
  const [videoOpen, setVideoOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [referOpen, setReferOpen] = useState(false);

  const openApply = () => setApplyOpen(true);
  const openRefer = () => setReferOpen(true);

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-h-screen"
      style={{ background: "var(--background)" }}
    >
      <Script
        src="https://fast.wistia.com/embed/medias/o5sxo5igwz.jsonp"
        strategy="lazyOnload"
        async
      />
      <Script src="https://fast.wistia.com/assets/external/E-v1.js" strategy="lazyOnload" async />

      <section className="relative overflow-y-clip pt-32">
        <HeroGlow align="center" />

        <div className="relative mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-24">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-left"
            >
              <EyebrowBadge>Hosting Program</EyebrowBadge>

              <h1
                className="mb-6 text-4xl leading-tight font-bold tracking-tight sm:text-5xl lg:text-6xl"
                style={{ color: "var(--foreground)" }}
              >
                Proxidize Hosting Program
              </h1>

              <p
                className="mb-10 text-lg leading-relaxed"
                style={{ color: "var(--muted-foreground)" }}
              >
                We&apos;re expanding our presence across the United States and we need your help to
                do it. We&apos;re launching a new Hosting Program through which you can earn passive
                income without any investment. You only need to host some equipment for us or refer
                somebody who gets selected.
              </p>

              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={openApply}
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-sm font-semibold transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                  style={{
                    background: "var(--foreground)",
                    color: "var(--background)",
                  }}
                >
                  Apply Now
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 8h10m0 0-4-4m4 4-4 4"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  onClick={openRefer}
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-sm font-semibold transition-all duration-200"
                  style={{
                    background: "transparent",
                    color: "var(--muted-foreground)",
                    border: "1px solid var(--border)",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      "color-mix(in srgb, var(--foreground) 20%, transparent)";
                    (e.currentTarget as HTMLElement).style.color = "var(--foreground)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--border)";
                    (e.currentTarget as HTMLElement).style.color = "var(--muted-foreground)";
                  }}
                >
                  Refer a Friend
                </button>
              </div>

              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                className="group mt-6 inline-flex items-center gap-2.5 text-sm font-medium transition-colors duration-200"
                style={{ color: "var(--muted-foreground)" }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.color = "var(--foreground)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.color = "var(--muted-foreground)";
                }}
              >
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full transition-all duration-200 group-hover:scale-105"
                  style={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <Play size={12} className="ml-0.5" fill="currentColor" />
                </span>
                Watch how to apply
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="w-full"
            >
              <div
                className="wistia_embed wistia_async_o5sxo5igwz seo=true videoFoam=true"
                style={{
                  width: "100%",
                  aspectRatio: "600 / 337",
                  position: "relative",
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "1px solid var(--border)",
                }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.5 }}
            className="mb-12 max-w-2xl lg:mb-16"
          >
            <h2
              className="mb-4 text-3xl leading-tight font-bold tracking-tight sm:text-4xl"
              style={{ color: "var(--foreground)" }}
            >
              What Do You Get?
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              In exchange for hosting our equipment, you will receive:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {BENEFITS.map((benefit, i) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={scrollRevealViewport}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="h-full rounded-xl p-6 lg:p-7"
                  style={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <div
                    className="mb-5 flex h-11 w-11 items-center justify-center rounded-lg"
                    style={{
                      background: "var(--surface-3)",
                      color: "var(--foreground)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <Icon size={18} />
                  </div>
                  <div
                    className="mb-2 text-xs font-semibold"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {benefit.highlight}
                  </div>
                  <h3
                    className="mb-2.5 text-lg font-semibold"
                    style={{ color: "var(--foreground)" }}
                  >
                    {benefit.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.5 }}
              className="order-2 lg:order-1"
            >
              <ProgramOverviewVisual />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="order-1 lg:order-2"
            >
              <h2
                className="mb-6 text-3xl leading-tight font-bold tracking-tight sm:text-4xl"
                style={{ color: "var(--foreground)" }}
              >
                Program Overview
              </h2>

              <div
                className="space-y-5 text-base leading-relaxed sm:text-lg"
                style={{ color: "var(--muted-foreground)" }}
              >
                <p>
                  We&apos;re looking for people like you to host our kits. They don&apos;t take up
                  much space — only about 10 square feet. You might have some free space in your
                  home or office.
                </p>
                <p>
                  If you meet the criteria below, Proxidize will send you the equipment, walk you
                  through how to set it up, and then we&apos;ll pay you between $200 and $1,600
                  every month, depending on the size of the kit we send you.
                </p>
                <p>
                  You don&apos;t need any technical expertise; we&apos;ll provide you with whatever
                  technical support you need to set the kit up. We also take on all the compliance
                  liabilities and fully commit to maintaining your privacy.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.5 }}
            className="mb-12 max-w-2xl lg:mb-16"
          >
            <h2
              className="mb-4 text-3xl leading-tight font-bold tracking-tight sm:text-4xl"
              style={{ color: "var(--foreground)" }}
            >
              How Does It Work?
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              Three simple steps from sign-up to your first payout — we handle the heavy lifting.
            </p>
          </motion.div>

          <div className="relative grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-[60px] right-[12%] left-[12%] hidden h-px md:block"
              style={{
                background:
                  "linear-gradient(to right, transparent, var(--border) 12%, var(--border) 88%, transparent)",
              }}
            />

            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const stepNumber = String(i + 1).padStart(2, "0");
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={scrollRevealViewport}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  className="relative h-full rounded-xl p-6 lg:p-7"
                  style={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <div className="mb-5 flex items-start justify-between">
                    <div
                      className="text-3xl font-bold tracking-tight lg:text-4xl"
                      style={{ color: "var(--foreground)" }}
                    >
                      {stepNumber}
                    </div>
                    <div
                      className="flex h-11 w-11 items-center justify-center rounded-lg"
                      style={{
                        background: "var(--surface-3)",
                        color: "var(--foreground)",
                        border: "1px solid var(--border)",
                      }}
                    >
                      <Icon size={18} />
                    </div>
                  </div>

                  <div
                    className="mb-2 text-[11px] font-semibold"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    Step {stepNumber}
                  </div>
                  <h3
                    className="mb-3 text-lg leading-snug font-semibold"
                    style={{ color: "var(--foreground)" }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {step.description}
                  </p>

                  {i < STEPS.length - 1 && (
                    <div
                      aria-hidden="true"
                      className="absolute top-[42px] -right-[14px] z-10 hidden h-7 w-7 items-center justify-center rounded-full md:flex"
                      style={{
                        background: "var(--surface-3)",
                        border: "1px solid var(--border)",
                        color: "var(--foreground)",
                      }}
                    >
                      <ChevronRight size={14} />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.5 }}
            >
              <h2
                className="mb-6 text-3xl leading-tight font-bold tracking-tight sm:text-4xl"
                style={{ color: "var(--foreground)" }}
              >
                What Do You Need to Join?
              </h2>

              <div
                className="space-y-5 text-base leading-relaxed sm:text-lg"
                style={{ color: "var(--muted-foreground)" }}
              >
                <p>
                  To set up the kit properly, you&apos;ll need 10 sq. ft. of dedicated space where
                  the kit won&apos;t get bumped or damaged. You&apos;ll also need a good and stable
                  internet connection via ethernet cable, with at least 100 Mbps speed and unlimited
                  data.
                </p>
                <p>
                  You should also have a reliable 4G LTE signal with a 20 Mbps minimum speed during
                  peak hours, plus a nearby power socket to plug the kit into — we&apos;ll even
                  include an extension cord to make things easier.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <RequirementsVisual />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-12 max-w-2xl text-center lg:mb-16"
          >
            <h2
              className="mb-4 text-3xl leading-tight font-bold tracking-tight sm:text-4xl"
              style={{ color: "var(--foreground)" }}
            >
              Zero Barriers, Zero Risks
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              Hosting a kit is designed to be simple, safe, and free for you from start to finish.
            </p>
          </motion.div>

          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-5 md:grid-cols-2 lg:gap-6">
            {ASSURANCES.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={scrollRevealViewport}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-5 rounded-xl p-6 lg:p-7"
                  style={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(91,140,255,0.12), rgba(91,140,255,0.04))",
                      border: "1px solid rgba(91,140,255,0.25)",
                      color: "var(--brand-green)",
                    }}
                  >
                    <Icon size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3
                      className="mb-2 text-lg leading-snug font-semibold"
                      style={{ color: "var(--foreground)" }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-12 flex justify-center lg:mt-14"
          >
            <button
              type="button"
              onClick={openApply}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-8 py-4 text-sm font-semibold transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
              style={{
                background: "var(--foreground)",
                color: "var(--background)",
              }}
            >
              Apply Now
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-4 w-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8h10m0 0-4-4m4 4-4 4" />
              </svg>
            </button>
          </motion.div>
        </div>
      </section>

      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.5 }}
              className="lg:sticky lg:top-24"
            >
              <div
                className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold"
                style={{
                  background: "rgba(91,140,255,0.10)",
                  color: "var(--brand-green)",
                  border: "1px solid rgba(91,140,255,0.25)",
                }}
              >
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: GREEN }}
                />
                Less than 5 minutes
              </div>

              <h2
                className="mb-5 text-3xl leading-tight font-bold tracking-tight sm:text-4xl"
                style={{ color: "var(--foreground)" }}
              >
                Apply Now! It Takes Less than 5 Minutes
              </h2>
              <p
                className="mb-8 text-base leading-relaxed sm:text-lg"
                style={{ color: "var(--muted-foreground)" }}
              >
                It only takes a few minutes to apply. Anyone can apply. All you have to do is
                collect some information about your internet and cellular connections, and fill out
                a form. Within 1–2 days of submission you&apos;ll hear from us about the next steps.
              </p>

              <button
                type="button"
                onClick={openApply}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-sm font-semibold transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                style={{
                  background: "var(--foreground)",
                  color: "var(--background)",
                }}
              >
                Apply Now
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-4 w-4"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8h10m0 0-4-4m4 4-4 4" />
                </svg>
              </button>
            </motion.div>

            <ol className="relative space-y-5">
              <div
                aria-hidden="true"
                className="absolute top-2 bottom-2 left-[19px] w-px"
                style={{ background: "var(--border)" }}
              />
              {APPLY_STEPS.map((step, i) => (
                <motion.li
                  key={step.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={scrollRevealViewport}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="relative flex gap-5"
                >
                  <div
                    className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold"
                    style={{
                      background: "var(--surface-3)",
                      border: "1px solid var(--border)",
                      color: "var(--foreground)",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div
                    className="flex-1 rounded-xl p-5 lg:p-6"
                    style={{
                      background: "var(--surface-2)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <h3
                      className="mb-2 text-lg leading-snug font-semibold"
                      style={{ color: "var(--foreground)" }}
                    >
                      {step.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      {step.description}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-12 max-w-2xl text-center lg:mb-16"
          >
            <h2
              className="mb-4 text-3xl leading-tight font-bold tracking-tight sm:text-4xl"
              style={{ color: "var(--foreground)" }}
            >
              What Are Existing Members Saying?
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              Real stories from people earning passive income hosting Proxidize kits.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={t.author}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={scrollRevealViewport}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="relative flex flex-col rounded-xl p-6 lg:p-7"
                style={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                }}
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 32 32"
                  className="mb-4 h-7 w-7"
                  style={{ color: "var(--brand-green)", opacity: 0.6 }}
                  fill="currentColor"
                >
                  <path d="M9.4 20.4H4.8c0-3.6.6-6.4 1.8-8.4 1.2-2 3.2-3.4 6-4.2v3.6c-1.5.6-2.6 1.4-3.3 2.5-.7 1.1-1 2.5-1 4.1h1.1c.6 0 1 .4 1 1v1.4zm14 0h-4.6c0-3.6.6-6.4 1.8-8.4 1.2-2 3.2-3.4 6-4.2v3.6c-1.5.6-2.6 1.4-3.3 2.5-.7 1.1-1 2.5-1 4.1h1.1c.6 0 1 .4 1 1v1.4z" />
                </svg>

                <blockquote
                  className="mb-6 flex-1 text-sm leading-relaxed"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  &ldquo;{t.quote}&rdquo;
                </blockquote>

                <figcaption
                  className="flex items-center gap-3 pt-5"
                  style={{ borderTop: "1px solid var(--border)" }}
                >
                  <AuthorAvatar
                    initial={t.author[0]}
                    color="rgba(91,140,255,0.18)"
                    className="h-9 w-9 text-sm"
                  />
                  <div className="min-w-0">
                    <div
                      className="text-sm leading-tight font-semibold"
                      style={{ color: "var(--foreground)" }}
                    >
                      {t.author}
                    </div>
                    <div className="mt-0.5 text-xs" style={{ color: "var(--muted-foreground)" }}>
                      {t.location}
                    </div>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.4, delay: 0.25 }}
            className="mt-12 flex justify-center lg:mt-14"
          >
            <button
              type="button"
              onClick={openApply}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-8 py-4 text-sm font-semibold transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
              style={{
                background: "var(--foreground)",
                color: "var(--background)",
              }}
            >
              Apply Now
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-4 w-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8h10m0 0-4-4m4 4-4 4" />
              </svg>
            </button>
          </motion.div>
        </div>
      </section>

      <section
        className="relative overflow-hidden border-t"
        style={{ borderColor: "var(--border)" }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(91,140,255,0.10), transparent 60%)",
          }}
        />

        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-2xl text-center"
          >
            <div className="relative mx-auto mb-10 w-full max-w-sm">
              <div className="flex items-center justify-between gap-3">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={scrollRevealViewport}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  className="flex flex-col items-center gap-2"
                >
                  <div
                    className="flex h-16 w-16 items-center justify-center rounded-full text-[11px] font-bold tracking-widest"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(91,140,255,0.22), rgba(91,140,255,0.06))",
                      border: "1.5px solid rgba(91,140,255,0.45)",
                      color: "var(--brand-green)",
                    }}
                  >
                    YOU
                  </div>
                </motion.div>

                <div className="relative flex h-16 flex-1 items-center justify-center">
                  <div
                    aria-hidden="true"
                    className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(90deg, var(--border) 0 5px, transparent 5px 10px)",
                    }}
                  />

                  <motion.span
                    initial={{ opacity: 0, y: -6, scale: 0.92 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={scrollRevealViewport}
                    transition={{
                      duration: 0.45,
                      delay: 0.25,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="relative z-10 inline-flex items-center rounded-full px-3 py-1.5 text-xs font-bold tracking-wide shadow-lg"
                    style={{
                      background: GREEN,
                      color: BRAND_BG,
                      boxShadow: "0 8px 24px -8px rgba(91,140,255,0.6)",
                    }}
                  >
                    +$200
                  </motion.span>
                </div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={scrollRevealViewport}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="flex flex-col items-center gap-2"
                >
                  <div
                    className="flex h-16 w-16 items-center justify-center rounded-full"
                    style={{
                      background: "var(--surface-3)",
                      border: "1.5px solid var(--border)",
                      color: "var(--foreground)",
                    }}
                  >
                    <Users size={22} />
                  </div>
                </motion.div>
              </div>

              <div
                className="mt-2 flex items-center justify-between px-2 text-[10px] font-semibold tracking-[0.18em] uppercase"
                style={{ color: "var(--muted-foreground)" }}
              >
                <span>You</span>
                <span>Friend</span>
              </div>
            </div>

            <h2
              className="mb-5 text-3xl leading-tight font-bold tracking-tight sm:text-4xl"
              style={{ color: "var(--foreground)" }}
            >
              Know Someone Who Qualifies?
            </h2>
            <p
              className="mb-8 text-base leading-relaxed sm:text-lg"
              style={{ color: "var(--muted-foreground)" }}
            >
              You don&apos;t need to be a Proxidize customer to join the Proxidize Hosting Program.
              You may not be able to join the program, but you might have a friend who qualifies!
              Earn $200 for every person you successfully refer to the hosting program. There&apos;s
              no limit, either!
            </p>

            <button
              type="button"
              onClick={openRefer}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-8 py-4 text-sm font-semibold transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
              style={{
                background: "var(--foreground)",
                color: "var(--background)",
              }}
            >
              <Users size={15} />
              Refer Friends
            </button>

            <div
              className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-medium"
              style={{ color: "var(--muted-foreground)" }}
            >
              <span>$200 per referral</span>
              <span aria-hidden="true" style={{ color: "var(--border)" }}>
                •
              </span>
              <span>No limit on referrals</span>
              <span aria-hidden="true" style={{ color: "var(--border)" }}>
                •
              </span>
              <span>No customer status required</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-10 max-w-2xl text-center"
          >
            <p className="mb-3 text-sm font-medium" style={{ color: "var(--brand-green)" }}>
              FAQ
            </p>
            <h2
              className="mb-4 text-3xl leading-tight font-bold tracking-tight sm:text-4xl"
              style={{ color: "var(--foreground)" }}
            >
              Frequently Asked Questions
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              Common questions about the Proxidize Hosting Program.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mx-auto max-w-3xl space-y-3"
          >
            {FAQS.map((faq) => (
              <details
                key={faq.q}
                className="group overflow-hidden rounded-xl"
                style={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                }}
              >
                <summary
                  className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-sm font-semibold select-none sm:text-base"
                  style={{ color: "var(--foreground)" }}
                >
                  <span className="leading-snug">{faq.q}</span>
                  <span
                    className="shrink-0 transition-transform duration-300 ease-out group-open:rotate-180"
                    style={{ color: "var(--muted-foreground)" }}
                    aria-hidden="true"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="size-4">
                      <path
                        d="M4 6L8 10L12 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </summary>
                <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-out group-open:grid-rows-[1fr]">
                  <div className="overflow-hidden">
                    <div
                      className="px-5 pt-1 pr-10 pb-5 text-sm leading-relaxed [&_code]:rounded [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs [&_li]:leading-relaxed [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&>*+*]:mt-3"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      {faq.a}
                    </div>
                  </div>
                </div>
              </details>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-12 text-center"
          >
            <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              Have any questions? Contact us at{" "}
              <a
                href={`mailto:${EMAIL.hosting}`}
                className="font-semibold transition-opacity hover:opacity-80"
                style={{ color: "var(--brand-green)" }}
              >
                {EMAIL.hosting}
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      <ModalShell
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        ariaLabel="Watch how to apply"
        size="video"
      >
        <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
          <button
            type="button"
            onClick={() => setVideoOpen(false)}
            aria-label="Close video"
            className="absolute -top-12 right-0 inline-flex h-9 w-9 items-center justify-center rounded-full transition-opacity hover:opacity-80"
            style={{
              background: "var(--surface-2)",
              color: "var(--foreground)",
              border: "1px solid var(--border)",
            }}
          >
            <X size={16} />
          </button>
          <iframe
            src={`${LOOM_EMBED_URL}?autoplay=1`}
            title="Watch how to apply — Proxidize Hosting Program"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 h-full w-full rounded-xl"
            style={{ border: "1px solid var(--border)" }}
          />
        </div>
      </ModalShell>

      <ApplyModal open={applyOpen} onClose={() => setApplyOpen(false)} />
      <ReferModal open={referOpen} onClose={() => setReferOpen(false)} />
    </main>
  );
}
